import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Select } from '@Sage/hoi-poi-ui';

import { getLiteral } from 'utils/getLiteral';

function fuzzyMatch(searchText, key) {
    if (!searchText || searchText.length === 0) return true;
    return key.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
}

const IntegrationStaticSelect = memo(
    ({
        className,
        data = [],
        disabled,
        error,
        hintText,
        isFullWidth = false,
        isFuzzySearch = true,
        onSelect,
        searchOnly = false,
        searchText,
    }) => {
        const options = useMemo(
            () =>
                data.map((item) => ({
                    label: item.text,
                    value: item.id,
                    rawItem: item,
                })),
            [data],
        );

        const selectedValue = useMemo(() => {
            if (searchOnly) return null;
            if (!searchText) return null;
            return options.find((opt) => opt.label === searchText) || null;
        }, [options, searchOnly, searchText]);

        const loadOptions = useCallback(
            (text) => {
                const filtered = isFuzzySearch
                    ? options.filter((opt) => fuzzyMatch(text, opt.label))
                    : options;
                return Promise.resolve(filtered);
            },
            [isFuzzySearch, options],
        );

        const handleChange = useCallback(
            (option) => {
                if (!option) return;
                const item = option.rawItem || data.find((entry) => entry.id === option.value);
                onSelect?.(item);
            },
            [data, onSelect],
        );

        const customOption = useCallback((option) => {
            const item = option.rawItem;
            if (item?.value) return item.value;
            return option.label;
        }, []);

        const selectOverrides = useMemo(
            () =>
                searchOnly
                    ? {
                          menu: { className: 'integration-multi-user-select-menu' },
                          option: { className: 'integration-multi-user-select-option' },
                      }
                    : undefined,
            [searchOnly],
        );

        const placeholder = useMemo(() => getLiteral(hintText || 'placeholder_select'), [hintText]);

        return (
            <Select
                alwaysLoadOnFocus={searchOnly}
                className={className}
                customOption={searchOnly ? customOption : undefined}
                error={error}
                isDisabled={disabled}
                isFullWidth={isFullWidth || searchOnly}
                isFuzzy
                loadOptions={loadOptions}
                onChange={handleChange}
                overrides={selectOverrides}
                placeholder={placeholder}
                placeholderReadOnly={placeholder}
                shouldSetValueOnChange={searchOnly ? false : undefined}
                value={selectedValue}
            />
        );
    },
);

IntegrationStaticSelect.displayName = 'IntegrationStaticSelect';

IntegrationStaticSelect.propTypes = {
    className: PropTypes.string,
    data: PropTypes.array,
    disabled: PropTypes.bool,
    error: PropTypes.any,
    hintText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    isFullWidth: PropTypes.bool,
    isFuzzySearch: PropTypes.bool,
    onSelect: PropTypes.func,
    searchOnly: PropTypes.bool,
    searchText: PropTypes.string,
};

export default IntegrationStaticSelect;
